import {
  BUSINESS_CYCLE_LATEST_PATH,
  getBusinessCycleBaseUrl,
} from "../config/businessCycleConfig";

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/** Maps AutoRyse's snake_case JSON to the camelCase shape used across the app. */
function adaptBusinessCycleResult(json) {
  if (!json || typeof json !== "object") return null;
  return {
    finalScenario: json.final_scenario ?? "",
    confidence: typeof json.confidence === "number" ? json.confidence : null,
    consensusSummary: json.consensus_summary ?? "",
    secondaryScenario: json.secondary_scenario ?? "",
    secondaryScenarioConditions: json.secondary_scenario_conditions ?? "",
    agentsConsidered:
      typeof json.agents_considered === "number" ? json.agents_considered : null,
    timestampUtc: json.timestamp_utc ?? "",
    curatedNews: Array.isArray(json.curated_news)
      ? json.curated_news.map((item) => ({
          title: item?.title ?? "",
          source: item?.source ?? "",
          date: item?.date ?? "",
          url: item?.url ?? "",
          selectionReason: item?.selection_reason ?? "",
        }))
      : [],
  };
}

/**
 * Fetches the currently detected market business cycle scenario (AutoRyse consensus).
 * Returns null when AutoRyse hasn't produced a result yet (proxy responds 404), so
 * callers can fall back to asking the user instead of treating this as a hard error.
 *
 * @param {AbortSignal} [signal]
 * @returns {Promise<{finalScenario:string, confidence:number|null, consensusSummary:string, secondaryScenario:string, secondaryScenarioConditions:string, agentsConsidered:number|null, timestampUtc:string, curatedNews:Array<{title:string,source:string,date:string,url:string,selectionReason:string}>}|null>}
 */
export async function fetchCurrentBusinessCycle(signal, retries = 2) {
  const url = `${getBusinessCycleBaseUrl()}${BUSINESS_CYCLE_LATEST_PATH}`;

  let lastErr;
  for (let attempt = 0; attempt < retries + 1; attempt++) {
    try {
      const res = await fetch(url, { method: "GET", signal });
      if (res.status === 404) {
        return null;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      return adaptBusinessCycleResult(json);
    } catch (e) {
      lastErr = e;
      if (signal?.aborted) {
        throw e;
      }
      const backoff = Math.min(1000 * 2 ** attempt, 4000);
      await sleep(backoff);
    }
  }
  throw lastErr ?? new Error("request_failed");
}
